import * as THREE from 'three';

/**
 * ===========================================================================
 *  VERTEX-ANIMATION-TEXTURE INSTANCED MATERIAL
 * ===========================================================================
 *
 * This is the piece that makes a Dynasty Warriors-scale crowd possible in a
 * browser. Instead of 500 SkinnedMeshes with 500 skeletons updated on the CPU,
 * every warrior is ONE instance of ONE InstancedMesh; the vertex shader reads
 * its bone matrices straight out of the baked animation texture with the same
 * skinIndex / skinWeight attributes the mesh already carries.
 *
 * Per-instance data
 *   instanceMatrix    position / yaw / scale        (three's own attribute)
 *   instanceColor     colourway tint                (three's own attribute)
 *   aAnimA  vec4      frameBase, frameCount, t01, cameraVisibility
 *   aAnimB  vec4      frameBase, frameCount, t01, blendWeight
 *   aMisc   vec4      faction, hitFlash, fade, seed
 *
 * Two poses are fetched and linearly blended when `blendWeight > 0`, which is
 * what gives the crowd real cross-fades and a walk<->run blend tree rather
 * than snapping between clips.
 *
 * It is grafted onto MeshStandardMaterial with onBeforeCompile so the crowd
 * keeps PBR lighting, the shared normal/ORM maps, fog, the environment probe
 * and real shadow casting — a hand-written ShaderMaterial would have to
 * reimplement all of it and would never match the hero.
 */

const VERT_PARS = /* glsl */`
attribute vec4 skinIndex;
attribute vec4 skinWeight;
attribute vec4 aAnimA;
attribute vec4 aAnimB;
attribute vec4 aMisc;
uniform sampler2D uVAT;
uniform vec4 uVATSize;   // w, h, 1/w, 1/h
uniform vec2 uVATCfg;    // framesPerRow, texelsPerFrame
varying vec2 vUvA;
varying float vFlash;
varying float vFade;
varying float vDis;
varying float vOcc;
vec4 gR0, gR1, gR2;

vec4 dwVatTexel(const in float frame, const in float bone, const in float r) {
  float row = floor(frame / uVATCfg.x);
  float col = (frame - row * uVATCfg.x) * uVATCfg.y + bone * 3.0 + r;
  return texture2D(uVAT, vec2((col + 0.5) * uVATSize.z, (row + 0.5) * uVATSize.w));
}
void dwVatAccum(const in float f, const in float bone, const in float w,
                inout vec4 R0, inout vec4 R1, inout vec4 R2) {
  R0 += w * dwVatTexel(f, bone, 0.0);
  R1 += w * dwVatTexel(f, bone, 1.0);
  R2 += w * dwVatTexel(f, bone, 2.0);
}
void dwVatPose(const in vec4 anim, out vec4 R0, out vec4 R1, out vec4 R2) {
  float f = anim.x + floor(clamp(anim.z, 0.0, 1.0) * max(anim.y - 1.0, 0.0) + 0.5);
  R0 = vec4(0.0); R1 = vec4(0.0); R2 = vec4(0.0);
  dwVatAccum(f, skinIndex.x, skinWeight.x, R0, R1, R2);
  if (skinWeight.y > 0.0) dwVatAccum(f, skinIndex.y, skinWeight.y, R0, R1, R2);
  if (skinWeight.z > 0.0) dwVatAccum(f, skinIndex.z, skinWeight.z, R0, R1, R2);
  if (skinWeight.w > 0.0) dwVatAccum(f, skinIndex.w, skinWeight.w, R0, R1, R2);
}
void dwVatSetup() {
  vec4 A0, A1, A2;
  dwVatPose(aAnimA, A0, A1, A2);
  float bw = aAnimB.w;
  if (bw > 0.002) {
    vec4 B0, B1, B2;
    dwVatPose(aAnimB, B0, B1, B2);
    gR0 = mix(A0, B0, bw); gR1 = mix(A1, B1, bw); gR2 = mix(A2, B2, bw);
  } else { gR0 = A0; gR1 = A1; gR2 = A2; }
  vec2 fo = vec2(step(0.5, aMisc.x) * step(aMisc.x, 1.5), step(1.5, aMisc.x));
  vUvA = uv * 0.5 + fo * 0.5;
  vFlash = aMisc.y;
  vFade = aMisc.z;
  vOcc = aAnimA.w;
  vDis = clamp(position.y * 0.52, 0.0, 1.0) * 0.62
       + fract(sin(dot(position.xz, vec2(12.9898, 78.233)) + aMisc.w) * 43758.5453) * 0.38;
}
vec3 dwSkinPos(const in vec3 p) { vec4 q = vec4(p, 1.0); return vec3(dot(gR0, q), dot(gR1, q), dot(gR2, q)); }
vec3 dwSkinNrm(const in vec3 n) { return vec3(dot(gR0.xyz, n), dot(gR1.xyz, n), dot(gR2.xyz, n)); }
`;

const FRAG_PARS = /* glsl */`
varying vec2 vUvA;
varying float vFlash;
varying float vFade;
varying float vDis;
varying float vOcc;
`;

const DEPTH_FRAG_PARS = /* glsl */`
varying float vFade;
varying float vDis;
varying float vOcc;
`;

function uniformsFor(vat) {
  return {
    uVAT: { value: vat.texture },
    uVATSize: { value: new THREE.Vector4(vat.width, vat.height, 1 / vat.width, 1 / vat.height) },
    uVATCfg: { value: new THREE.Vector2(vat.framesPerRow, vat.texelsPerFrame) },
  };
}

/**
 * @param {THREE.MeshStandardMaterial} base   a chars faction material to clone
 * @param {object} vat                        result of bakeVAT()
 * @param {THREE.Texture} atlas               combined faction albedo
 */
export function makeCrowdMaterial(base, vat, atlas) {
  const m = base ? base.clone() : new THREE.MeshStandardMaterial({ roughness: 0.85, metalness: 0.1 });
  m.name = 'crowdVAT';
  if (atlas) m.map = atlas;
  m.vertexColors = true;
  m.side = THREE.FrontSide;
  m.shadowSide = THREE.DoubleSide;
  const U = uniformsFor(vat);
  m.onBeforeCompile = (shader) => {
    Object.assign(shader.uniforms, U);
    shader.vertexShader = shader.vertexShader
      .replace('#include <common>', '#include <common>\n' + VERT_PARS)
      .replace('#include <beginnormal_vertex>',
        'dwVatSetup();\n vec3 objectNormal = normalize(dwSkinNrm(normal));')
      .replace('#include <begin_vertex>', 'vec3 transformed = dwSkinPos(position);');
    shader.fragmentShader = shader.fragmentShader
      .replace('#include <common>', '#include <common>\n' + FRAG_PARS)
      .replace('#include <clipping_planes_fragment>',
        /* Camera occlusion is a whole-instance decision. `vOcc` is strictly
         * binary, so every fragment of one blocker takes the same branch and
         * close bodies can never become perforated during a transition. Death
         * dissolve remains independent in vDis/vFade. */
        `#include <clipping_planes_fragment>
         if (vDis > vFade || vOcc < 0.5) discard;`)
      .replace('#include <map_fragment>', /* glsl */`
        #ifdef USE_MAP
          diffuseColor *= texture2D( map, vUvA );
        #endif
        // Square the state so the contact is crisp but the tail clears quickly.
        // Multiplicative lift retains the atlas/faction hue instead of replacing
        // every simultaneously-hit body with the same white material.
        float dwHit = vFlash * vFlash;
        diffuseColor.rgb *= 1.0 + dwHit * 0.14;
      `)
      .replace('#include <emissivemap_fragment>', /* glsl */`
        #include <emissivemap_fragment>
        float dwDead = 1.0 - step(0.999, vFade);
        float dwEdge = (1.0 - smoothstep(0.0, 0.20, vFade - vDis)) * dwDead;
        totalEmissiveRadiance += vec3(1.5, 0.62, 0.20) * dwEdge * 3.0;
        // Bias the existing body colour slightly warm rather than adding a
        // uniform warm-white slab. This keeps red/blue faction reads intact.
        vec3 dwHitTint = mix(diffuseColor.rgb, vec3(1.0, 0.42, 0.16), 0.18);
        totalEmissiveRadiance += dwHitTint * dwHit * 0.42;
      `);
  };
  m.customProgramCacheKey = () => 'dwCrowdVAT-occ4-hit2';
  m.needsUpdate = true;
  return m;
}

/** Matching depth material so the crowd casts animated shadows. */
export function makeCrowdDepthMaterial(vat) {
  const d = new THREE.MeshDepthMaterial({ depthPacking: THREE.RGBADepthPacking });
  d.name = 'crowdVATDepth';
  const U = uniformsFor(vat);
  d.onBeforeCompile = (shader) => {
    Object.assign(shader.uniforms, U);
    shader.vertexShader = shader.vertexShader
      .replace('#include <common>', '#include <common>\n' + VERT_PARS)
      .replace('#include <begin_vertex>', 'dwVatSetup();\n vec3 transformed = dwSkinPos(position);');
    shader.fragmentShader = shader.fragmentShader
      .replace('#include <common>', '#include <common>\n' + DEPTH_FRAG_PARS)
      .replace('#include <clipping_planes_fragment>',
        // Match the colour pass's binary decision exactly: a hidden blocker
        // leaves no orphan hero-adjacent shadow; visible LOD0 bodies stay solid.
        '#include <clipping_planes_fragment>\n if (vDis > vFade || vOcc < 0.5) discard;');
  };
  d.customProgramCacheKey = () => 'dwCrowdVATDepth-occ4';
  d.needsUpdate = true;
  return d;
}
